type StorageData = {
	[key: string]: {
		[subKey: string]: any;
	};
};

/**
 * Gets data from localStorage for a specific step
 * @param mainKey - The main storage key (e.g., 'p_auth')
 * @param subKey - The sub key for the step (e.g., 's_p')
 * @returns The stored data or null if not found
 */
export const getStepData = (mainKey: string, subKey?: string): any | null => {
	if (typeof window === 'undefined') return null;

	const storedData = localStorage.getItem(mainKey);
	if (!storedData) return null;

	try {
		const parsedData: StorageData = JSON.parse(storedData);

		if (!subKey) {
			return parsedData;
		}

		return parsedData[subKey] || null;
	} catch (error) {
		console.error('Error parsing localStorage data:', error);
		return null;
	}
};

/**
 * Saves data to localStorage for a specific step
 * @param mainKey - The main storage key (e.g., 'p_auth')
 * @param subKey - The sub key for the step (e.g., 's_p')
 * @param data - The data to store
 */
export const saveStepData = (
	mainKey: string,
	subKey: string,
	data: any,
): void => {
	if (typeof window === 'undefined') return;

	// 1. Get existing data (or initialize empty object)
	const existingDataStr = localStorage.getItem(mainKey);
	let existingData: Record<string, any> = existingDataStr
		? JSON.parse(existingDataStr)
		: {};

	// 2. Completely remove the subKey if it exists
	if (existingData[subKey] !== undefined) {
		delete existingData[subKey];
	}

	// 3. Assign new data to the subKey
	existingData[subKey] = data;

	// 4. Save back to localStorage
	try {
		localStorage.setItem(mainKey, JSON.stringify(existingData));
	} catch (error) {
		console.error('Error saving to localStorage:', error);
	}
};

/**
 * Clears all data for a specific main key
 * @param mainKey - The main storage key to clear
 */
export const clearStepData = (mainKey: string): void => {
	if (typeof window === 'undefined') return;
	localStorage.removeItem(mainKey);
};
